import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

interface PollingConfig<T> {
  key: string;                     // Unique identifier for this polling instance
  fetchFn: () => Promise<T>;       // Function that fetches the data
  interval?: number;               // Polling interval in ms (default: 5000)
  enabled?: boolean;               // Whether polling is enabled
  onSuccess?: (data: T) => void;   // Optional callback on successful fetch
  onError?: (error: Error) => void;// Optional callback on error
  errorMessage?: string;           // Custom error message
  showToast?: boolean;             // Whether to show error toasts
}

export function usePolling<T>({
  key,
  fetchFn,
  interval = 5000,
  enabled = true,
  onSuccess,
  onError,
  errorMessage = 'Failed to fetch data',
  showToast = true,
}: PollingConfig<T>) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastPollTime, setLastPollTime] = useState<Date | null>(null);

  // Stabilize the fetch function with useCallback
  const fetch = useCallback(async () => {
    const now = new Date();
    const timeSinceLastPoll = lastPollTime ? now.getTime() - lastPollTime.getTime() : null;
    
    if (timeSinceLastPoll && timeSinceLastPoll < interval * 0.9) {
      // console.log(`[${key}] Skipping poll - too soon (${timeSinceLastPoll/1000}s < ${interval/1000}s)`);
      return;
    }

    // console.log(`[${key}] Polling at ${now.toISOString()}${timeSinceLastPoll ? `, ${timeSinceLastPoll/1000}s since last poll` : ''}`);
    setIsLoading(true);

    try {
      const result = await fetchFn();
      // console.log(`[${key}] Response:`, result);
      setData(result);
      setError(null);
      onSuccess?.(result);
    } catch (err) {
      const error = err as Error;
      setError(error);
      onError?.(error);
      if (showToast) {
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
      setLastPollTime(now);
    }
  }, [key, fetchFn, interval, lastPollTime, onSuccess, onError, errorMessage, showToast]);

  // Use a ref to maintain the latest enabled state
  const enabledRef = useRef(enabled);
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  // Set up the polling interval
  useEffect(() => {
    if (!enabled) {
      // console.log(`[${key}] Polling disabled`);
      return;
    }

    // console.log(`[${key}] Starting polling with ${interval/1000}s interval`);
    fetch(); // Initial fetch

    const intervalId = setInterval(() => {
      if (enabledRef.current) {
        fetch();
      }
    }, interval);

    return () => {
      // console.log(`[${key}] Cleaning up polling`);
      clearInterval(intervalId);
    };
  }, [key, interval, enabled]); // Removed fetch from dependencies

  return {
    data,
    error,
    isLoading,
    isPolling: enabled,
    refetch: fetch,
    lastPollTime
  };
} 